import { i18n } from '@boot/i18n.js';

/**
 * Formats time duration using localized day/hour/minute/second labels.
 * @param {Object} params - Time components
 * @param {number} params.day - Number of days
 * @param {number} params.hour - Number of hours
 * @param {number} params.min - Number of minutes
 * @param {number} params.sec - Number of seconds
 * @returns {string} Formatted time string (e.g., '2d 5h 30m 15s')
 * @private
 */
function dhmsFormat({ day, hour, min, sec }) {
    const dayString = day > 0 ? `${day}${i18n.global.t('d')} ` : '';
    const hourString = hour > 0 ? `${hour}${i18n.global.t('h')} ` : '';
    const minString = min > 0 ? `${min}${i18n.global.t('m')} ` : '';
    const secString = sec > 0 ? `${sec}${i18n.global.t('s')} ` : '';

    // Remove second string if day > 0
    if (day > 0) {
        return `${dayString}${hourString}${minString}`;
    }

    return `${dayString}${hourString}${minString}${secString}`;
}

/**
 * Formats time duration using colon-separated format with zero-padding.
 * @param {Object} params - Time components
 * @param {number} params.day - Number of days
 * @param {number} params.hour - Number of hours
 * @param {number} params.min - Number of minutes
 * @param {number} params.sec - Number of seconds
 * @returns {string} Formatted time string (e.g., '02:05:30:15' or '05:30:15')
 * @private
 */
function dhmsColonFormat({ day, hour, min, sec }) {
    function prependZero(value) {
        if (value <= 9) {
            return `0${value}`;
        } else {
            return value;
        }
    }

    let time = `${prependZero(hour)}:${prependZero(min)}:${prependZero(sec)}`;
    if (day > 0) {
        time = `${prependZero(day)}:` + time;
    }

    return time;
}

/**
 * Converts a UTC datetime string to a localized datetime string in the specified timezone.
 * @param {string} utcTime - UTC datetime string (e.g., '2021-07-06T14:40:50Z')
 * @param {string} timeZone - IANA timezone identifier (e.g., 'America/Los_Angeles')
 * @param {string} [locale='en-us'] - BCP 47 language tag for localization
 * @returns {string} Localized datetime string (e.g., '7/6/2021, 7:40:50 PM')
 * @throws {TypeError} If utcTime or timeZone are not strings
 */
export function convertUTCTimeToLocaleTimezone(
    utcTime,
    timeZone,
    locale = 'en-us'
) {
    if (typeof utcTime === 'string' && typeof timeZone === 'string') {
        const convertedLocalTime = new Date(utcTime).toLocaleString(locale, {
            timeZone: timeZone
        });
        return convertedLocalTime;
    } else {
        throw TypeError;
    }
}

/**
 * Converts milliseconds to hours/minutes/seconds or days/hours/minutes/seconds format.
 * @param {number} ms - Duration in milliseconds
 * @param {boolean} colonFormat - If true, uses colon format (e.g., '01:23:45' or '01:02:23:45'),
 *                               if false, uses labeled format (e.g., '1h 23m 45s' or '1d 2h 23m 45s')
 * @returns {string} Formatted duration string
 */
export function convertMillisecondsToDHMS(ms, colonFormat) {
    // calculate days, hours, minutes, seconds
    const day = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hour = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const min = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const sec = Math.floor((ms % (1000 * 60)) / 1000);

    // format
    if (ms > 0) {
        if (colonFormat) {
            return dhmsColonFormat({ day, hour, min, sec });
        } else {
            return dhmsFormat({ day, hour, min, sec });
        }
    } else {
        if (colonFormat) {
            return '00:00:00';
        } else {
            return `0${i18n.global.t('h')} 0${i18n.global.t(
                'm'
            )} 0${i18n.global.t('s')}`;
        }
    }
}

/**
 * Converts a duration from seconds to minutes with 2 decimal places.
 * @param {number} durationSeconds - Duration in seconds
 * @param {string} [userLocale='en-US'] - BCP 47 language tag for number formatting
 * @returns {string} Duration in minutes with 2 decimal places (e.g., '7.40')
 *                   Negative zero is converted to positive zero
 */
export function convertDurationToMinutes(
    durationSeconds,
    userLocale = 'en-US'
) {
    const minutes = durationSeconds / 60;
    const formattedMinutes = minutes.toLocaleString(userLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    // Prevent negative zero
    // e.g. Before formatting -0.004590 => After formatting -0.00
    if (formattedMinutes === '-0.00') {
        return '0.00';
    }
    return formattedMinutes;
}

/**
 * Converts a duration from seconds to days/hours/minutes/seconds format.
 * @param {number} durationSeconds - Duration in seconds
 * @param {boolean} colonFormat - If true, uses colon format (e.g., '01:23:45'),
 *                               if false, uses labeled format (e.g., '1h 23m 45s')
 * @returns {string} Formatted duration string
 */
export function convertDurationToDHMS(durationSeconds, colonFormat) {
    return convertMillisecondsToDHMS(durationSeconds * 1000, colonFormat);
}

/**
 * Generates a formatted date/time label based on the specified resolution.
 * @param {string} date - ISO date string
 * @param {string} resolution - Time resolution ('monthly', 'weekly', 'daily', or 'hourly')
 * @param {string} userLanguage - language tag for date/time localization
 * @returns {string} Formatted date/time label.
 * @example
 * getDateLabel('2024-10-21T00:00:00Z', 'monthly', 'en-US')
 * // => 'Oct 2024'
 * getDateLabel('2024-10-21T00:00:00Z', 'weekly', 'en-US')
 * // => 'Oct 21, 2024\nSunday'
 * getDateLabel('2024-10-21T00:00:00Z', 'hourly', 'en-US')
 * // => 'Oct 21, 2024 at 12:00 PM'
 */
export function getDateLabel(date, resolution, userLanguage) {
    const dateObject = new Date(date);
    const year = dateObject.getFullYear();

    const dateFormatter =
        dateObject
            .toLocaleString(userLanguage, {
                day: 'numeric',
                month: 'short'
            })
            .replace(',', '') + `, ${year}`;
    const weekday = dateObject.toLocaleString(userLanguage, {
        weekday: 'long'
    });

    switch (resolution) {
        case 'monthly':
            return dateObject.toLocaleString(userLanguage, {
                month: 'long',
                year: 'numeric'
            });
        case 'weekly':
        case '7-day':
        case 'daily':
            return `${dateFormatter}\n${weekday}`;
        default: {
            // hourly
            const hour = dateObject.toLocaleString(userLanguage, {
                hour: 'numeric',
                minute: 'numeric',
                hour12: false
            });
            return `${dateFormatter}\n${hour}`;
        }
    }
}

/**
 * Get the current time and day of week in a specific timezone.
 * @param {string} timezone - IANA timezone string (e.g., 'America/New_York', 'UTC')
 * @returns {Object} Object containing currentTime (HH:MM:SS) and currentDayKey (e.g., 'monday')
 */
export function getCurrentTimeInTimezone(timezone = 'UTC') {
    const now = new Date();

    // Convert current time to facility timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        weekday: 'short'
    });

    const parts = formatter.formatToParts(now);
    const hour = parts.find(p => p.type === 'hour').value;
    const minute = parts.find(p => p.type === 'minute').value;
    const second = parts.find(p => p.type === 'second').value;
    const weekday = parts.find(p => p.type === 'weekday').value;

    const currentTime = `${hour}:${minute}:${second}`;

    // Map weekday abbreviation to day property name
    const weekdayMap = {
        Sun: 'sunday',
        Mon: 'monday',
        Tue: 'tuesday',
        Wed: 'wednesday',
        Thu: 'thursday',
        Fri: 'friday',
        Sat: 'saturday'
    };

    const currentDayKey = weekdayMap[weekday];

    return { currentTime, currentDayKey };
}

/**
 * Check if a given time falls within a shift's time range.
 * Handles shifts that span midnight (e.g., 22:00 to 06:00).
 * @param {string} currentTime - Current time in HH:MM:SS format
 * @param {string} startTime - Shift start time in HH:MM:SS format
 * @param {string} endTime - Shift end time in HH:MM:SS format
 * @returns {boolean} True if current time is within the shift range
 */
export function isTimeInShiftRange(currentTime, startTime, endTime) {
    // Handle shifts that span midnight (e.g., 22:00 to 06:00)
    if (endTime < startTime) {
        // Shift spans midnight
        return currentTime >= startTime || currentTime < endTime;
    }

    // Normal shift within same day
    return currentTime >= startTime && currentTime < endTime;
}

/**
 * Determines whether a shift is active for a given day and time.
 * The function checks if the shift is scheduled for the specified day (e.g., 'monday') and
 * whether the current time falls within the shift's start and end hours. Handles shifts that
 * span midnight (e.g., 22:00 to 06:00) by using isTimeInShiftRange.
 *
 * @param {Object} shift - Shift object with day properties (e.g., { monday: true }) and start_hour/end_hour (HH:MM:SS)
 * @param {string} currentTime - Current time in HH:MM:SS format
 * @param {string} currentDayKey - Day property name (e.g., 'monday', 'tuesday')
 * @returns {boolean} True if shift is active
 *
 * @example
 * // Example shift object for a night shift on Monday
 * const shift = {
 *   monday: true,
 *   start_hour: '22:00:00',
 *   end_hour: '06:00:00'
 * };
 * isShiftActive(shift, '23:30:00', 'monday'); // returns true
 * isShiftActive(shift, '07:00:00', 'monday'); // returns false
 */
export function isShiftActive(shift, currentTime, currentDayKey) {
    // Check if shift is active for current day
    if (!shift[currentDayKey]) {
        return false;
    }

    // Check if current time falls within shift hours
    return isTimeInShiftRange(currentTime, shift.start_hour, shift.end_hour);
}
